import React from 'react';
import { TouchableOpacity, TouchableOpacityProps } from 'react-native';
import styled from 'styled-components/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { Theme } from '@theme/aiaTheme';
import { useTheme } from '@theme/ThemeProvider';
import { Text } from './Text';

type ChipVariant = 'filled' | 'outlined' | 'tonal';
type ChipSize = 'sm' | 'md';

interface ChipProps extends Omit<TouchableOpacityProps, 'style'> {
  variant?: ChipVariant;
  size?: ChipSize;
  selected?: boolean;
  icon?: string;
  onDelete?: () => void;
  children: React.ReactNode;
}

interface StyledChipProps {
  variant: ChipVariant;
  size: ChipSize;
  selected: boolean;
  disabled: boolean;
}

const StyledChip = styled(TouchableOpacity)<StyledChipProps>`
  flex-direction: row;
  align-items: center;
  border-radius: ${({ theme }) => theme.radius.full}px;
  padding-horizontal: ${({ theme, size }) =>
    size === 'sm' ? theme.spacing.md : theme.spacing.base}px;
  padding-vertical: ${({ theme, size }) => (size === 'sm' ? theme.spacing.xs : theme.spacing.sm)}px;
  background-color: ${({ theme, variant, selected, disabled }) => {
    if (disabled) return theme.colors.neutral200;
    if (selected) return theme.colors.brandPrimary;
    switch (variant) {
      case 'filled':
        return theme.colors.brandSecondary;
      case 'tonal':
        return theme.colors.neutral100;
      case 'outlined':
        return theme.colors.transparent;
      default:
        return theme.colors.neutral100;
    }
  }};
  border-width: ${({ variant }) => (variant === 'outlined' ? 1 : 0)}px;
  border-color: ${({ theme, selected }) =>
    selected ? theme.colors.brandPrimary : theme.colors.border};
`;

const ChipText = styled.Text<{
  variant: ChipVariant;
  selected: boolean;
  disabled: boolean;
}>`
  color: ${({ theme, variant, selected, disabled }) => {
    if (disabled) return theme.colors.textDisabled;
    if (selected || variant === 'filled') return theme.colors.textOnPrimary;
    return theme.colors.textPrimary;
  }};
  font-size: ${({ theme }) => theme.typography.bodySmall.fontSize}px;
  line-height: ${({ theme }) => theme.typography.bodySmall.lineHeight}px;
  font-weight: ${({ theme }) => theme.typography.bodySmall.fontWeight};
`;

const IconWrapper = styled.View<{ position: 'left' | 'right' }>`
  margin-${({ position }) => (position === 'left' ? 'right' : 'left')}: ${({ theme }) =>
    theme.spacing.xs}px;
`;

export const Chip: React.FC<ChipProps> = ({
  variant = 'tonal',
  size = 'md',
  selected = false,
  disabled = false,
  icon,
  onDelete,
  children,
  ...props
}) => {
  const theme = useTheme();
  const iconSize = size === 'sm' ? 14 : 16;
  const iconColor =
    selected || variant === 'filled' ? theme.colors.textOnPrimary : theme.colors.textSecondary;

  return (
    <StyledChip
      variant={variant}
      size={size}
      selected={selected}
      disabled={disabled}
      activeOpacity={0.7}
      {...props}
    >
      {icon && (
        <IconWrapper position="left">
          <Icon name={icon} size={iconSize} color={iconColor} />
        </IconWrapper>
      )}
      <ChipText variant={variant} selected={selected} disabled={disabled}>
        {children}
      </ChipText>
      {onDelete && (
        <TouchableOpacity onPress={onDelete} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <IconWrapper position="right">
            <Icon name="close" size={iconSize} color={iconColor} />
          </IconWrapper>
        </TouchableOpacity>
      )}
    </StyledChip>
  );
};
